import React, { useState, useEffect } from "react";
import { Row, Col, Input, Label, Button } from "reactstrap";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import ProporcionModal from "../../../ProporcionModal";
import ArticuloRapidoModal from "../../../ArticuloRapidoModal";
import { FotoArticulo } from "../../../FotoArticulo";

// Edicion completa de un pedido en una sola pantalla: arriba sus datos (tipo,
// paqueteria, OC del cliente, observaciones) y abajo sus articulos con la misma
// captura que PedidosCreate: un bloque por modelo (su renglon) con la reja
// color x talla y su boton de proporcion.
//
// - Las celdas que ya existian se editan sobre su renglon de ArticulosPedido;
//   no pueden quedar por debajo de lo ya surtido.
// - Las celdas nuevas (otro color u otra talla, o un modelo agregado) se dan de
//   alta al guardar, con el numero de renglon de su bloque.
// - Un modelo sin nada surtido se puede quitar: sus renglones se dan de baja.
const URL_ARTICULOS_PEDIDO = process.env.REACT_APP_URL_ARTICULOS_PEDIDO;
const URL_PEDIDOS = process.env.REACT_APP_URL_PEDIDOS;
const URL_LOGS = process.env.REACT_APP_URL_LOGS;

const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const llave = (c, t) => `${c}|${t}`;
const num = (n) => new Intl.NumberFormat("es-MX").format(Number(n) || 0);
const dinero = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n) || 0);

// Colores o tallas del modelo: si el articulo los declara se usan esos (resueltos
// contra el catalogo); si no, el catalogo completo, igual que en PedidosCreate.
const resuelve = (lista, catalogo) => {
  if (!lista || !lista.length) return [...catalogo];
  return lista.map((x) => (x && x.name ? x : catalogo.find((c) => String(c._id) === String(x && x._id ? x._id : x)))).filter(Boolean);
};
// Piso de una linea: no puede bajar de lo surtido; si un dato viejo ya traia
// mas surtido que cantidad, el piso es la cantidad que tenia.
const pisoLinea = (l) => Math.min(l.surtido || 0, l.cantidad || 0);

// Reparte la cantidad de una celda entre sus lineas (normalmente una sola; dos si
// un dato viejo repitio la variante en el mismo renglon): primero el piso de cada
// una, luego hasta lo que traia, y lo que sobre a la primera.
function reparteEnLineas(lineas, total) {
  const nuevas = lineas.map((l) => pisoLinea(l));
  let resto = total - nuevas.reduce((s, n) => s + n, 0);
  lineas.forEach((l, i) => {
    const sube = Math.max(0, Math.min(resto, (l.cantidad || 0) - nuevas[i]));
    nuevas[i] += sube; resto -= sube;
  });
  if (resto > 0) nuevas[0] += resto;
  return nuevas;
}

const agrega = (lista, item) => (item && !lista.some((x) => String(x._id) === String(item._id)) ? [...lista, item] : lista);

function EditarArticulosPedido({ pedido, articulos, coloresCat, tallasCat, user, onRegresar }) {
  const [bloques, setBloques] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [impuestos, setImpuestos] = useState(Number(pedido.impuestos) || 0);
  const [descuento, setDescuento] = useState(Number(pedido.descuento) || 0);
  // datos del pedido
  const [tipo, setTipo] = useState(pedido.tipo || "Pedido");
  const [paqueteria, setPaqueteria] = useState(pedido.paqueteria && pedido.paqueteria !== "NA" ? pedido.paqueteria : "");
  const [ocCliente, setOcCliente] = useState(pedido.ordenCompraCliente && pedido.ordenCompraCliente !== "NA" ? pedido.ordenCompraCliente : "");
  const [observaciones, setObservaciones] = useState(pedido.observaciones && pedido.observaciones !== "NA" ? pedido.observaciones : "");
  const [colores, setColores] = useState(coloresCat || []);
  const [tallas, setTallas] = useState(tallasCat || []);
  // proporcion y alta rapida: sobre que bloque
  const [propBloque, setPropBloque] = useState(null);
  const [altaBloque, setAltaBloque] = useState(null);
  // modelos dados de alta aqui mismo (se suman a la lista del buscador)
  const [extraArts, setExtraArts] = useState([]);
  const puedePrecio = !!user.cambiaPrecios;

  useEffect(() => { setColores(coloresCat || []); }, [coloresCat]);
  useEffect(() => { setTallas(tallasCat || []); }, [tallasCat]);

  // Bloque vacio armado desde un articulo del catalogo
  const bloqueDe = (art, renglon, nuevo) => {
    const cols = resuelve(art?.colores, colores);
    const tals = resuelve(art?.tallas, tallas);
    return {
      key: uuidv4(), nuevo, renglon, quitar: false,
      articulo: art?._id || "", nombre: art ? `${art.codigo} ${art.nombre}` : "", urlFoto: art?.urlFoto || "",
      precio: Number(art?.venta) || 0, colores: cols, tallas: tals, celdas: {},
    };
  };

  useEffect(() => {
    setCargando(true);
    axios.get(`${URL_ARTICULOS_PEDIDO}/pedidos/${pedido.id}`, { headers: headers() })
      .then((r) => {
        const lineas = (r.data || []).filter((a) => a.is_active === "Si" && a.articulos?.[0]?._id);
        const porBloque = new Map();
        lineas.forEach((l) => {
          const art = l.articulos[0];
          const kb = `${art._id}|${l.renglon || 1}`;
          if (!porBloque.has(kb)) {
            const delCatalogo = articulos.find((a) => String(a._id) === String(art._id)) || art;
            const b = bloqueDe(delCatalogo, l.renglon || 1, false);
            b.nombre = `${art.codigo} ${art.nombre}`;
            b.urlFoto = art.urlFoto || b.urlFoto;
            b.precio = Number(l.precio) || 0;
            porBloque.set(kb, b);
          }
          const b = porBloque.get(kb);
          const col = l.colores?.[0], tal = l.tallas?.[0];
          b.colores = agrega(b.colores, col);
          b.tallas = agrega(b.tallas, tal);
          const k = llave(col?._id, tal?._id);
          const linea = { id: l._id, cantidad: Number(l.cantidad) || 0, surtido: Number(l.surtido) || 0, precio: Number(l.precio) || 0 };
          const c = b.celdas[k] || { lineas: [], cantidad: 0, original: 0, surtido: 0, minimo: 0, precio: linea.precio };
          c.lineas.push(linea);
          c.cantidad += linea.cantidad; c.original += linea.cantidad;
          c.surtido += linea.surtido; c.minimo += pisoLinea(linea);
          b.celdas[k] = c;
        });
        setBloques([...porBloque.values()].sort((a, b) => a.renglon - b.renglon));
        setCargando(false);
      })
      .catch((e) => {
        setCargando(false);
        Swal.fire({ icon: "error", title: "No se pudieron traer los articulos del pedido", text: e.message });
      });
  }, [pedido.id]); // eslint-disable-line

  const cambiaBloque = (key, cambio) => setBloques((prev) => prev.map((b) => (b.key === key ? { ...b, ...cambio(b) } : b)));

  const cambiaCelda = (key, idC, idT, valor) =>
    cambiaBloque(key, (b) => {
      const k = llave(idC, idT);
      const actual = b.celdas[k] || { cantidad: 0, surtido: 0, minimo: 0 };
      return { celdas: { ...b.celdas, [k]: { ...actual, cantidad: valor === "" ? "" : Number(valor) } } };
    });

  // Lo que reparte la proporcion: cada celda del bloque toma su cantidad (0 si no
  // le toco), sin bajar de lo ya surtido.
  function aplicaProporcion(lineas) {
    let respetadas = 0;
    cambiaBloque(propBloque, (b) => {
      const celdas = { ...b.celdas };
      b.colores.forEach((c) => b.tallas.forEach((t) => {
        const k = llave(c._id, t._id);
        const l = lineas.find((x) => x.colores === c._id && x.tallas === t._id);
        const actual = celdas[k] || { cantidad: 0, surtido: 0, minimo: 0 };
        let cantidad = l ? l.cantidad : 0;
        if (cantidad < (actual.minimo || 0)) { cantidad = actual.minimo; respetadas += 1; }
        if (celdas[k] || cantidad > 0) celdas[k] = { ...actual, cantidad };
      }));
      return { celdas };
    });
    if (respetadas > 0) {
      setTimeout(() => Swal.fire({ icon: "info", title: "Se respeto lo ya surtido",
        text: `${respetadas} celda(s) quedaron en lo que ya se surtio; no pueden bajar de ahi.` }), 300);
    }
  }

  const agregaModelo = () => setBloques((prev) => [...prev, bloqueDe(null, 0, true)]);

  const eligeModelo = (key, art) => {
    if (!art) return;
    const nuevo = bloqueDe(art, 0, true);
    cambiaBloque(key, () => ({ articulo: nuevo.articulo, nombre: nuevo.nombre, urlFoto: nuevo.urlFoto, precio: nuevo.precio, colores: nuevo.colores, tallas: nuevo.tallas, celdas: {} }));
  };

  const quitaBloque = (b) => {
    if (b.nuevo) { setBloques((prev) => prev.filter((x) => x.key !== b.key)); return; }
    cambiaBloque(b.key, (x) => ({ quitar: !x.quitar }));
  };

  // Totales
  const precioCelda = (b, c) => (c.lineas && !puedePrecio ? c.precio : Number(b.precio) || 0);
  const vivos = bloques.filter((b) => !b.quitar && b.articulo);
  const piezasBloque = (b) => Object.values(b.celdas).reduce((s, c) => s + (Number(c.cantidad) || 0), 0);
  const importeBloque = (b) => Object.values(b.celdas).reduce((s, c) => s + (Number(c.cantidad) || 0) * precioCelda(b, c), 0);
  const piezas = vivos.reduce((s, b) => s + piezasBloque(b), 0);
  const pendientes = vivos.reduce((s, b) => s + Object.values(b.celdas).reduce((x, c) => x + Math.max(0, (Number(c.cantidad) || 0) - (c.surtido || 0)), 0), 0);
  const subTotal = vivos.reduce((s, b) => s + importeBloque(b), 0);
  const descuentoNumero = (subTotal * (Number(descuento) || 0)) / 100;
  const iva = ((Number(impuestos) || 0) * (subTotal - descuentoNumero)) / 100;
  const total = subTotal - descuentoNumero + iva + (Number(pedido.comisionTDC) || 0) + (Number(pedido.comisionFlete) || 0);

  async function guardar() {
    // nada por debajo de lo surtido
    const bajo = [];
    vivos.forEach((b) => Object.values(b.celdas).forEach((c) => {
      if ((Number(c.cantidad) || 0) < (c.minimo || 0)) bajo.push(`${b.nombre}`);
    }));
    if (bajo.length) {
      Swal.fire({ icon: "warning", title: "Por debajo de lo surtido", text: `Revisa ${[...new Set(bajo)].join(", ")}: una cantidad quedo por debajo de lo ya surtido.` });
      return;
    }
    if (bloques.some((b) => b.nuevo && !b.articulo)) {
      Swal.fire({ icon: "warning", title: "Falta el modelo", text: "Hay un renglon nuevo sin modelo: eligelo o quitalo." });
      return;
    }
    const conf = await Swal.fire({
      title: "¿Guardar los cambios del pedido?",
      html: `${num(piezas)} piezas · ${dinero(total)}`,
      icon: "question", showCancelButton: true, confirmButtonText: "Si, guardar", cancelButtonText: "Cancelar",
    });
    if (!conf.isConfirmed) return;

    setGuardando(true);
    try {
      // los modelos nuevos se numeran despues del ultimo renglon del pedido
      let ultimo = bloques.reduce((m, b) => Math.max(m, b.renglon || 0), 0);
      for (const b of bloques) {
        if (b.nuevo && b.quitar) continue;
        const renglon = b.nuevo ? (ultimo += 1) : b.renglon;
        for (const [k, c] of Object.entries(b.celdas)) {
          const [idC, idT] = k.split("|");
          const cantidad = Number(c.cantidad) || 0;
          const precio = precioCelda(b, c);
          if (c.lineas) {
            const nuevas = b.quitar ? c.lineas.map(() => 0) : reparteEnLineas(c.lineas, cantidad);
            for (const [i, l] of c.lineas.entries()) {
              const q = nuevas[i];
              const precioL = puedePrecio ? precio : l.precio;
              // modelo quitado, o linea en cero sin nada surtido: se da de baja
              if (q === 0 && !l.surtido) {
                await axios.patch(`${URL_ARTICULOS_PEDIDO}/${l.id}`,
                  { is_active: "No", autorizadoApartado: "No", autorizadoSurtido: "No" }, { headers: headers() });
              } else if (q !== l.cantidad || precioL !== l.precio) {
                await axios.patch(`${URL_ARTICULOS_PEDIDO}/${l.id}`, {
                  cantidad: q, precio: precioL, total: Number((q * precioL).toFixed(2)), pendiente_surtir: Math.max(0, q - (l.surtido || 0)),
                }, { headers: headers() });
              }
            }
          } else if (!b.quitar && cantidad > 0) {
            await axios.post(URL_ARTICULOS_PEDIDO, {
              pedidos: pedido.id, renglon, articulos: b.articulo, colores: idC, tallas: idT,
              cantidad, cantidadProcesos: 0, surtido: 0, pendiente_surtir: cantidad,
              precio, total: Number((cantidad * precio).toFixed(2)),
              clientes: pedido.idCliente, fecha: pedido.fecha,
            }, { headers: headers() });
          }
        }
      }
      await axios.patch(`${URL_PEDIDOS}/${pedido.id}`, {
        total_general: Number(total.toFixed(2)),
        total_cantidad: piezas,
        piezasPendientes: pendientes,
        editArticulos: user.id,
        editPedido: user.id,
        observaciones: observaciones.trim() || "NA",
        ordenCompraCliente: ocCliente.trim() || "NA",
        paqueteria: paqueteria.trim() || "NA",
        // Cambiar el tipo desde aqui equivale a autorizar/desautorizar el prepedido:
        // un Pedido aparta inventario y se puede surtir; un Prepedido no.
        tipo,
        autorizadoPrepedido: tipo === "Pedido" ? "Si" : "No",
        autorizadoApartado: tipo === "Pedido" ? "Si" : "No",
        comisionTDC: Number(pedido.comisionTDC) || 0,
        comisionFlete: Number(pedido.comisionFlete) || 0,
        subTotal: Number(subTotal.toFixed(2)),
        iva: Number(iva.toFixed(2)),
        impuestos: Number(impuestos) || 0,
        descuento: Number(descuento) || 0,
        editado: "Si",
        fechaModificado: moment().format("YYYY-MM-DD"),
      }, { headers: headers() });
      await axios.post(URL_LOGS, { tipo: "Editar Pedido", detalle: `${pedido.numero}`, user: user.id }, { headers: headers() }).catch(() => {});
      setGuardando(false);
      await Swal.fire({ icon: "success", title: "Pedido actualizado", timer: 1200, showConfirmButton: false });
      window.location.reload();
    } catch (error) {
      setGuardando(false);
      Swal.fire({ icon: "error", title: "No se pudo guardar", text: error.response?.data?.message || error.message,
        footer: "Lo que alcanzo a guardarse ya quedo; revisa el pedido antes de volver a guardar." });
    }
  }

  const bloqueProp = bloques.find((b) => b.key === propBloque);
  const opciones = [...articulos, ...extraArts].sort((a, b) => (a.codigo > b.codigo ? 1 : -1));

  return (
    <div className="card container col-md-12">
      <h4>Editar pedido {pedido.numero}</h4>
      <h5 style={{ color: "#64748b" }}>Cliente: {pedido.cliente}</h5>

      <Row style={{ marginBottom: 10 }}>
        <Col md={2}>
          <Label>Tipo</Label>
          <Input type="select" value={tipo} onChange={(e) => setTipo(e.target.value)}
            title="Un Pedido aparta inventario y se puede surtir; un Prepedido no">
            <option value="Pedido">Pedido</option>
            <option value="Prepedido">Prepedido</option>
          </Input>
        </Col>
        <Col md={2}>
          <Label>OC del cliente</Label>
          <Input value={ocCliente} onChange={(e) => setOcCliente(e.target.value)} />
        </Col>
        <Col md={2}>
          <Label>Paquetería</Label>
          <Input value={paqueteria} onChange={(e) => setPaqueteria(e.target.value)} />
        </Col>
        <Col md={6}>
          <Label>Observaciones</Label>
          <Input type="textarea" rows={1} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
        </Col>
      </Row>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ width: 110 }}>
          <Label>Impuestos %</Label>
          <Input type="number" min="0" step="any" value={impuestos} onChange={(e) => setImpuestos(e.target.value)} />
        </div>
        <div style={{ width: 110 }}>
          <Label>Descuento %</Label>
          <Input type="number" min="0" step="any" value={descuento} onChange={(e) => setDescuento(e.target.value)} />
        </div>
        <div style={{ width: 150 }}><Label>Descuento $</Label><Input value={dinero(descuentoNumero)} disabled /></div>
        <div style={{ width: 160 }}><Label>Sub Total</Label><Input value={dinero(subTotal)} disabled /></div>
        <div style={{ width: 150 }}><Label>IVA</Label><Input value={dinero(iva)} disabled /></div>
        <div style={{ width: 160 }}><Label>Total</Label><Input value={dinero(total)} disabled /></div>
        <div style={{ width: 110 }}><Label>Piezas</Label><Input value={num(piezas)} disabled /></div>
      </div>

      {cargando ? <p style={{ color: "#64748b" }}>Cargando los articulos del pedido...</p> : null}

      {bloques.map((b) => {
        const surtidoBloque = Object.values(b.celdas).reduce((s, c) => s + (c.surtido || 0), 0);
        return (
          <div key={b.key} style={{ marginBottom: 14, opacity: b.quitar ? 0.45 : 1 }}>
            <Row className="align-items-end">
              <Col md={3}>
                <Label style={{ fontSize: "0.72rem", color: "#94a3b8", fontWeight: 700 }}>
                  {b.nuevo ? "MODELO NUEVO EN EL PEDIDO" : `RENGLON #${b.renglon}`}
                  {b.quitar ? " · SE QUITA AL GUARDAR" : ""}
                </Label>
                {b.nuevo ? (
                  <Autocomplete
                    size="small"
                    options={opciones}
                    value={opciones.find((a) => String(a._id) === String(b.articulo)) || null}
                    onChange={(e, art) => eligeModelo(b.key, art)}
                    getOptionLabel={(o) => `${o.codigo} ${o.nombre}`}
                    renderInput={(params) => <TextField {...params} label="Selecciona el modelo" variant="outlined" />}
                  />
                ) : (
                  <Input value={b.nombre} disabled />
                )}
              </Col>
              <Col md={2}>
                <Label>Precio</Label>
                <Input type="number" min="0" step="any" value={b.precio} disabled={!puedePrecio && !b.nuevo}
                  title={!puedePrecio && !b.nuevo ? "Necesitas el permiso de cambiar precios" : undefined}
                  onChange={(e) => { const v = e.target.value; cambiaBloque(b.key, () => ({ precio: v })); }} />
              </Col>
              <Col md={2}><Label>Piezas</Label><Input value={num(piezasBloque(b))} disabled /></Col>
              <Col md={2}><Label>Importe</Label><Input value={dinero(importeBloque(b))} disabled /></Col>
              <Col md={3} style={{ display: "flex", gap: 6, alignItems: "center", paddingBottom: 4 }}>
                <Button size="sm" color="primary" disabled={!b.articulo || b.quitar}
                  title="Repartir un total entre los colores y tallas del modelo con su proporcion (ej. 2-1-1 y 1-2-2-1)"
                  onClick={() => setPropBloque(b.key)}>
                  <i className="fas fa-percent" /> Proporción
                </Button>
                {b.nuevo ? (
                  <Button size="sm" color="warning" title="Dar de alta un modelo que no existe"
                    onClick={() => setAltaBloque(b.key)}><i className="fas fa-plus-square" /></Button>
                ) : null}
                <Button size="sm" color={b.quitar ? "secondary" : "danger"} disabled={surtidoBloque > 0}
                  title={surtidoBloque > 0 ? "Ya tiene piezas surtidas: no se puede quitar" : b.quitar ? "Dejar el modelo en el pedido" : "Quitar este modelo del pedido"}
                  onClick={() => quitaBloque(b)}>
                  <i className={b.quitar ? "fas fa-undo" : "fas fa-minus"} />
                </Button>
              </Col>
            </Row>

            {b.articulo ? (
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start", margin: "4px 0 0 0", padding: "8px 10px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", overflowX: "auto" }}>
                {/* la foto es del modelo: si va en dos renglones, cambia en los dos */}
                <FotoArticulo idArticulo={b.articulo} url={b.urlFoto} tipo="modelo"
                  onSubida={(url) => setBloques((prev) => prev.map((x) => (String(x.articulo) === String(b.articulo) ? { ...x, urlFoto: url } : x)))} />
                <div>
                <div style={{ display: "flex", gap: 6, alignItems: "flex-end", marginBottom: 4 }}>
                  <div style={{ width: 130 }} />
                  {b.tallas.map((t) => (
                    <div key={t._id} style={{ width: 70, textAlign: "center", fontSize: "0.68rem", fontWeight: 700, color: "#64748b" }}>{t.name}</div>
                  ))}
                  <div style={{ width: 70, textAlign: "center", fontSize: "0.68rem", fontWeight: 700, color: "#64748b" }}>TOTAL</div>
                </div>
                {b.colores.map((col) => {
                  const sumaColor = b.tallas.reduce((s, t) => s + (Number(b.celdas[llave(col._id, t._id)]?.cantidad) || 0), 0);
                  return (
                    <div key={col._id} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                      <div style={{ width: 130, fontSize: "0.78rem", fontWeight: 600 }}>{col.name}</div>
                      {b.tallas.map((t) => {
                        const c = b.celdas[llave(col._id, t._id)];
                        return (
                          <div key={t._id} style={{ width: 70 }}>
                            <Input bsSize="sm" type="number" min={c?.minimo || 0} disabled={b.quitar}
                              style={{ textAlign: "right", padding: "2px 4px" }}
                              value={c ? c.cantidad : 0}
                              onChange={(e) => cambiaCelda(b.key, col._id, t._id, e.target.value)} />
                            {c?.surtido ? (
                              <div style={{ fontSize: "0.6rem", textAlign: "right", color: "#b45309" }}>surt. {num(c.surtido)}</div>
                            ) : null}
                          </div>
                        );
                      })}
                      <div style={{ width: 70, textAlign: "right", fontSize: "0.8rem", fontWeight: 700 }}>{num(sumaColor)}</div>
                    </div>
                  );
                })}
                </div>
              </div>
            ) : null}
          </div>
        );
      })}

      <div style={{ display: "flex", gap: 8, margin: "6px 0 18px 0" }}>
        <Button color="info" size="sm" onClick={agregaModelo} title="Agregar otro modelo al pedido">
          <i className="fas fa-plus" /> Agregar modelo
        </Button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <Button color="success" onClick={guardar} disabled={guardando || cargando}>
          {guardando ? "Guardando..." : "Guardar"}
        </Button>
        <Button color="danger" onClick={onRegresar} disabled={guardando}>Regresar</Button>
      </div>

      <ProporcionModal
        abierto={!!propBloque}
        cerrar={() => setPropBloque(null)}
        coloresCat={bloqueProp ? bloqueProp.colores : []}
        tallasCat={bloqueProp ? bloqueProp.tallas : []}
        onAplicar={aplicaProporcion}
        onNuevoColor={(c) => {
          setColores((l) => agrega(l, c));
          cambiaBloque(propBloque, (b) => ({ colores: agrega(b.colores, c) }));
        }}
        onNuevaTalla={(t) => {
          setTallas((l) => agrega(l, t));
          cambiaBloque(propBloque, (b) => ({ tallas: agrega(b.tallas, t) }));
        }}
        catalogoColores={colores}
        catalogoTallas={tallas}
        marcarTodos
        titulo="Proporcion del modelo por color y talla"
      />

      <ArticuloRapidoModal
        abierto={!!altaBloque}
        cerrar={() => setAltaBloque(null)}
        coloresCat={colores}
        tallasCat={tallas}
        onCatalogoNuevo={(tipo, nuevo) => (tipo === "color" ? setColores : setTallas)((l) => agrega(l, nuevo))}
        onCreado={(art) => {
          // el modelo recien creado trae sus colores y tallas como ids
          const nuevo = bloqueDe(art, 0, true);
          cambiaBloque(altaBloque, () => ({ articulo: nuevo.articulo, nombre: nuevo.nombre, urlFoto: nuevo.urlFoto, precio: nuevo.precio, colores: nuevo.colores, tallas: nuevo.tallas, celdas: {} }));
          setExtraArts((l) => [...l, art]);
        }}
      />
    </div>
  );
}

export default EditarArticulosPedido;
